export type PauseReason = 'user' | 'focus-loss' | 'visibility-hidden';
export type PauseListener = (paused: boolean, reasons: readonly PauseReason[]) => void;

const PAUSE_REASON_ORDER: readonly PauseReason[] = ['user', 'focus-loss', 'visibility-hidden'];

export class PauseController {
  readonly #reasons = new Set<PauseReason>();
  readonly #listeners = new Set<PauseListener>();

  public isPaused(): boolean {
    return this.#reasons.size > 0;
  }

  public getReasons(): readonly PauseReason[] {
    return PAUSE_REASON_ORDER.filter((reason) => this.#reasons.has(reason));
  }

  public request(reason: PauseReason): void {
    this.#update(reason, true);
  }

  public release(reason: PauseReason): void {
    this.#update(reason, false);
  }

  public toggleUserPause(): void {
    this.#update('user', !this.#reasons.has('user'));
  }

  public subscribe(listener: PauseListener): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #update(reason: PauseReason, active: boolean): void {
    if (this.#reasons.has(reason) === active) return;
    if (active) this.#reasons.add(reason);
    else this.#reasons.delete(reason);
    const isPaused = this.isPaused();
    const reasons = this.getReasons();
    for (const listener of this.#listeners) listener(isPaused, reasons);
  }
}

export class BrowserPauseCoordinator {
  public constructor(private readonly pause: PauseController) {}

  public handleWindowFocus(focused: boolean): void {
    if (focused) this.pause.release('focus-loss');
    else this.pause.request('focus-loss');
  }

  public handleVisibility(hidden: boolean): void {
    if (hidden) this.pause.request('visibility-hidden');
    else this.pause.release('visibility-hidden');
  }
}
